import Vue from 'vue';
import Color from 'color';
import { getSlideId, getTriggers } from '../utils';
import { prepNodeForStack } from '../utils/node';

const baseEdetailerURL = process.env.VUE_APP_BASE_EDETAILER_URL ?? process.env.BASE_EDETAILER_URL;

const state = () => ({
  ablyReady: false,
  ampHistory: [],
  amps: {},
  ampSetupOptions: null,
  stack: {},
  previewAmp: null,
  iframeUrl: '',
  isOpen: false,
  isPreviewRunning: false,
  isSlideLoading: false,
  isAmpLoading: false,
  triggersDisabled: false,
  storage: {},
  crmStorage: {},
  conversationOngoing: false,
  currentConversation: null,
  latestConversation: {},
  currentNode: null,
  highlightedNode: null,
  showNudge: false,
  compactNudgeVisible: false,
  triggeredConversation: null,
  triggeredTriggers: [],
  delayTimers: {},
  showSelfdetailerErrorPage: false,
  showPasswordPage: false,
  crmAccountIdentifier: null,
  crmConnector: null,
  crmLoading: true,
});

const getDimmedColor = (colorObj) => {
  const color = colorObj.alpha(1);
  const isDark = color.isDark();
  const dimmed = isDark ? color.mix(Color('white'), 0.95) : color.darken(0.5);

  return dimmed.hex();
};

const getAmpConfig = (amp) => {
  const color = Color(amp?.color || '#6200EA');

  return {
    name: amp?.name,
    color: color.hex(),
    colorDimmed: getDimmedColor(color),
    textColor: color.isDark() ? '#ffffff' : '#000000',
    textDimmedColor: color.isDark() ? color.hex() : '#ffffff',
    icon: amp?.icon || 'chatbubble_op1',
    avatar: amp?.avatarUrl,
  };
};

const getters = {
  ampHasMenu: (_localstate, { currentAmp }) => {
    const ampMenu = currentAmp?.data?.menu || {};
    const keys = Object.keys(ampMenu);
    return (
      keys.length > 0 &&
      keys.some((k) => (typeof ampMenu[k] === 'object' ? Object.keys(ampMenu[k]) : ampMenu[k]).length > 0)
    );
  },

  currentAmpId: (localstate) => localstate.ampHistory[localstate.ampHistory.length - 1],

  currentAmp: (localstate, { currentAmpId }) => localstate.amps[currentAmpId] || {},

  isResponsive: (_localstate, { currentAmp }) => currentAmp?.responsive,

  ampOptions: (localstate) => localstate.ampSetupOptions,

  getAmpById: (localstate) => (id) => localstate.amps[id] || {},

  rootAmp: (localstate) => localstate.amps[localstate.ampHistory[0]] || null,

  currentStack: (localState, { currentAmpId }) => localState.stack[currentAmpId],

  isReady: (localstate) => !localstate.conversationOngoing && !localstate.triggersDisabled,

  isPreview: (localstate) => !!localstate.previewAmp,

  isPreviewRunning: (localstate) => localstate.isPreviewRunning,

  nextNode: (localstate) => (parentId) => {
    if (!localstate.currentConversation) {
      return null;
    }

    return Object.values(localstate.currentConversation?.nodes || {}).find((node) =>
      // if parentId is not defined ensure that node.parentId is also not defined
      // null, undefined and ''(empty string) means the same thing in this case
      !parentId ? !node.parentId : node.parentId === parentId
    );
  },

  currentNode: (localstate) => localstate.currentNode,

  lastStackNode: (localstate, { currentAmpId }) => {
    const stack = localstate.stack[currentAmpId] || [];
    return stack[stack.length - 1];
  },

  getLatestConversationId: (localstate) => localstate.latestConversation?.id || null,

  getNode: (localstate) => (id) =>
    Object.values(localstate.currentConversation?.nodes || {}).find((node) => node.id === id),

  getConversation:
    (localstate, { currentAmp, rootAmp }) =>
    (id) => {
      const currentConversations = currentAmp?.data?.conversations || {};
      const rootConversations = rootAmp?.data?.conversations || {};

      return currentConversations[id] ?? rootConversations[id];
    },

  getConversationBySlide:
    (localstate, { currentAmp }) =>
    (slide) =>
      Object.values(currentAmp.data.conversations).find(
        (conv) => conv.slide && getSlideId(conv.slide) === getSlideId(slide)
      ),

  getConversationByNode:
    (localstate, { currentAmp }) =>
    (nodeId) =>
      Object.values(currentAmp.data.conversations).find(
        (conv) => Object.values(conv.nodes || {}).find(n => n.id === nodeId)
      ),

  getConversationByIframeUrl: ({ iframeUrl }, { getConversationBySlide }) => {
    const urlSplit = iframeUrl.split('/');
    let slideName = urlSplit[urlSplit.length - 2]; // second last part of the string is the slide name
    slideName = decodeURIComponent(slideName);
    return getConversationBySlide(slideName);
  },

  currentConversationId: (localstate) => localstate.currentConversation?.id,

  currentConversation: (_localstate, { currentConversationId, getConversation }) =>
    currentConversationId && getConversation(currentConversationId),

  getTriggers: (localstate, { currentAmp, rootAmp }) => {
    const currentConversations = currentAmp?.data?.conversations ?? {};
    const rootConversations = rootAmp?.data?.conversations ?? {};

    return [...getTriggers(currentConversations), ...getTriggers(rootConversations)];
  },

  wasTriggered: (localstate) => (id) => localstate.triggeredTriggers?.includes(id),

  ampConfig: (localstate, { currentAmp, isPreview }) => {
    const amp = isPreview ? localstate.previewAmp : currentAmp;

    return getAmpConfig(amp);
  },

  rootAmpConfig: (localstate, { rootAmp }) => {
    return getAmpConfig(rootAmp);
  },

  getStorageId:
    (_, { getAmpStorage: ampStorage }) =>
    (key) =>
      Object.keys(ampStorage).find((k) => ampStorage[k] === key),

  getStorageKey:
    (_, { getAmpStorage: ampStorage }) =>
    (keyId) =>
      ampStorage[keyId],

  getStorageValue:
    (_, { getStorage: storage }) =>
    (key) =>
      storage[key],

  getStorage: (localState) => {
    return { ...localState.storage, ...localState.crmStorage };
  },

  getAmpStorage: (localState, { currentAmp }) => currentAmp?.data?.storage ?? {},

  getOpenState: (localState) => localState.isOpen,

  getSlide:
    (_, { getConversation }) =>
    (conversationId) => {
      const conversation = getConversation(conversationId);

      if (conversation?.slide) {
        return conversation.slide;
      }

      if (conversation?.parentId) {
        const parentConversation = getConversation(conversation.parentId);

        if (parentConversation?.slide) {
          return parentConversation.slide;
        }
      }

      return null;
    },

  getTriggeredConversation: (localState) => localState.triggeredConversation,

  showStackedEdetail: (localstate, { currentAmp, isPreview }) =>
    localstate.ampHistory.length > 1 && currentAmp.ampType === 'edetailer' && !isPreview,
};

const mutations = {
  SET_ABLY_READY(localstate) {
    localstate.ablyReady = true;
  },

  SET_SELFDETAILER_ERROR_PAGE(localstate, show) {
    localstate.showSelfdetailerErrorPage = show;
  },

  SET_PASSWORD_PAGE(localstate, show) {
    localstate.showPasswordPage = show;
  },

  SET_AMP_OPTIONS(localstate, options) {
    localstate.ampSetupOptions = options;
  },

  PUSH_AMP(localstate, amp) {
    const { conversations } = amp.data;

    Object.keys(conversations).forEach((key) => {
      conversations[key] = { ...conversations[key], ampId: amp.id };
    });

    localstate.amps = { ...localstate.amps, [amp.id]: { ...amp, data: { ...amp.data, conversations } } };
    localstate.triggersDisabled = false;

    if (!localstate.stack[amp.id]) {
      localstate.stack[amp.id] = [];
    }

    const historyIndex = localstate.ampHistory.findIndex((id) => id === amp.id);

    if (historyIndex < 0) {
      localstate.ampHistory = [...localstate.ampHistory, amp.id];
    }
  },

  POP_AMP(localState) {
    const removedAmp = localState.ampHistory.pop();
    delete localState.amps[removedAmp.id];
  },

  GO_TO_AMP(localstate, ampId) {
    const historyCopy = [...localstate.ampHistory];
    const index = historyCopy.findIndex((id) => id === ampId);

    localstate.ampHistory = historyCopy.slice(0, index + 1);
  },

  SET_CURRENT_AMP(localstate, ampId) {
    localstate.currentAmpId = ampId;
  },

  SET_IFRAME_URL(localstate, url) {
    localstate.iframeUrl = url;
  },

  SET_PREVIEW_AMP(localstate, amp) {
    localstate.previewAmp = amp;
  },

  SET_IS_PREVIEW_RUNNING(localstate, value) {
    localstate.isPreviewRunning = value;
  },

  SET_CONVERSATION(localstate, conversation) {
    localstate.conversationOngoing = !!conversation;
    localstate.currentConversation = conversation;

    if (conversation) {
      localstate.latestConversation = conversation;
    }

    localstate.triggersDisabled = !!conversation;
    localstate.currentNode = undefined;
  },

  USE_TRIGGER(localState, triggerId) {
    const { triggeredTriggers } = localState;

    if (!triggeredTriggers?.includes(triggerId)) {
      localState.triggeredTriggers = [...triggeredTriggers, triggerId];
    }
  },

  SET_CURRENT_NODE(localstate, node) {
    localstate.currentNode = node;
  },

  ADD_NODE(localstate, { node, currentAmpId }) {
    if (node) {
      const currentNodes = localstate.stack?.[currentAmpId] || [];

      localstate.stack = {
        ...(localstate.stack || {}),
        [currentAmpId]: [
          ...currentNodes,
          {
            node,
            conversation: localstate.currentConversation.id,
            storage: { ...(localstate.storage || {}) },
            delayed: !!node.delay,
          },
        ],
      };
    }
  },

  SET_HIGHLIGHTED_NODE(localstate, node) {
    localstate.highlightedNode = node;
  },

  ADD_TO_STORAGE(localstate, { key, data, crm }) {
    if (crm) Vue.set(localstate.crmStorage, key, data);
    else Vue.set(localstate.storage, key, data);
  },

  SET_OPEN_STATE(localstate, open) {
    localstate.isOpen = !!open;
  },

  SET_IS_SLIDE_LOADING(localstate, isLoading) {
    localstate.isSlideLoading = isLoading;
  },

  SET_IS_AMP_LOADING(localstate, isLoading) {
    localstate.isAmpLoading = isLoading;
  },

  SET_NUDGE_STATE(localstate, show) {
    localstate.showNudge = !!show;
  },

  SET_COMPACT_NUDGE_VISIBLE(localstate, visible) {
    localstate.compactNudgeVisible = !!visible;
  },

  SET_TRIGGERED_CONVERSATION(localstate, conversation) {
    localstate.triggeredConversation = conversation;
  },

  UPDATE_STACK_ITEM(localstate, { index, nodeId, ...updates }) {
    const currentAmpId = localstate.ampHistory[localstate.ampHistory.length - 1];
    const nodeStack = Object.entries(localstate.stack).find((entry) => entry[1].map((n) => n.node.id).includes(nodeId));

    const ampId = nodeStack ? nodeStack[0] : currentAmpId;
    const nodeIndex = index >= 0 ? index : localstate.stack[ampId]?.findLastIndex(({ node }) => node.id === nodeId);

    if (nodeIndex >= 0 && localstate.stack[ampId]?.length) {
      const nodeCopy = { ...localstate.stack[ampId][nodeIndex] };

      Object.keys(updates).forEach((update) => {
        nodeCopy[update] = updates[update];
      });

      localstate.stack[ampId].splice(nodeIndex, 1, nodeCopy);
    }
  },

  SAVE_DELAY_TIMER(localstate, { nodeId, delayTimer, reject }) {
    localstate.delayTimers[nodeId] = { delayTimer, reject };
  },

  CLEAR_STATE(localstate) {
    Object.values(localstate.delayTimers).forEach(({ delayTimer, reject }) => {
      clearTimeout(delayTimer);
      if (reject) reject();
    });

    localstate.delayTimers = {};
    localstate.stack = {};
    localstate.storage = {};
  },

  CLEAR_AMP_HISTORY(localstate) {
    localstate.ampHistory = [];
    localstate.amps = {};

    // ensure that the new conversation will not trigger a nudge
    localstate.currentConversation = null;
    localstate.conversationOngoing = false;

    // remove the stored password for edetail rendering
    localStorage.removeItem('edetailPassword');
  },

  SET_CRM_ACCOUNT_IDENTIFIER(localstate, data) {
    localstate.crmAccountIdentifier = data;
  },

  SET_CRM_CONNECTOR(localstate, data) {
    localstate.crmConnector = data;
  },

  SET_CRM_LOADING(localstate, data) {
    localstate.crmLoading = data;
  },
};

export const actions = {
  setAblyReady({ commit }) {
    commit('SET_ABLY_READY');
  },

  setCrmAccountId({ commit }, data) {
    commit('SET_CRM_ACCOUNT_IDENTIFIER', data);
  },

  setSelfdetailerErrorPage({ commit }, show) {
    commit('SET_SELFDETAILER_ERROR_PAGE', show);
  },

  setPasswordPage({ commit }, show) {
    commit('SET_PASSWORD_PAGE', show);
  },

  setAmpSetupOptions({ commit }, options) {
    commit('SET_AMP_OPTIONS', options);
  },

  pushAmp({ commit }, amp) {
    commit('PUSH_AMP', amp);
  },

  popAmp({ commit }) {
    commit('POP_AMP');
  },

  goToAmp({ commit }, ampId) {
    commit('GO_TO_AMP', ampId);
  },

  setIframeUrl({ commit, getters: { getSlide, currentAmp } }, conversationId) {
    if (!conversationId) {
      commit('SET_IFRAME_URL', '');
      return;
    }

    let slideName = getSlide(conversationId);

    if (!slideName) {
      commit('SET_IFRAME_URL', '');
      return;
    }

    const { id, organizationId } = currentAmp;
    slideName = encodeURIComponent(slideName).replace(/^(v[^%\W]+)%2F/, (_, version) => `${version}/`);

    commit('SET_IFRAME_URL', `${baseEdetailerURL}/${organizationId}/${id}/${slideName}/index.html`);
  },

  setPreviewAmp({ commit }, amp) {
    commit('SET_PREVIEW_AMP', amp);
  },

  setIsPreviewRunning({ commit }, value) {
    commit('SET_IS_PREVIEW_RUNNING', value);
  },

  setupConversation({ commit }, conversation) {
    commit('SET_CONVERSATION', conversation);
  },

  addNode({ commit, getters: { currentAmpId, isPreview, isPreviewRunning, getStorage: storage } }, nodeData) {
    const node = prepNodeForStack(nodeData, { isPreview, isPreviewRunning, storage });

    commit('ADD_NODE', { node, currentAmpId });
    commit('SET_CURRENT_NODE', node);
  },

  setCurrentNode({ commit }, node) {
    commit('SET_CURRENT_NODE', node);
  },

  setHighlightedNode({ commit }, node) {
    commit('SET_HIGHLIGHTED_NODE', node);
  },

  addToStorage({ commit, getters: { getStorageKey } }, { key, data, crm = false }) {
    const storageKey = getStorageKey(key);

    if (storageKey || crm) {
      commit('ADD_TO_STORAGE', { key: crm ? key : storageKey, data, crm });
    }
  },

  setIsSlideLoading({ commit }, isLoading) {
    commit('SET_IS_SLIDE_LOADING', isLoading);
  },

  setIsAmpLoading({ commit }, isLoading) {
    commit('SET_IS_AMP_LOADING', isLoading);
  },

  setOpenState({ commit }, open) {
    commit('SET_OPEN_STATE', open);
  },

  setNudgeState({ commit }, show) {
    commit('SET_NUDGE_STATE', show);
  },

  setCompactNudgeVisible({ commit }, visible) {
    commit('SET_COMPACT_NUDGE_VISIBLE', visible);
  },

  setTriggeredConversation({ commit }, conversation) {
    commit('SET_TRIGGERED_CONVERSATION', conversation);
  },

  activateTrigger({ commit }, triggerId) {
    commit('USE_TRIGGER', triggerId);
  },

  endConversation({ commit }) {
    commit('SET_CONVERSATION', null);
  },

  saveDelay({ commit }, { nodeId, delayTimer, reject }) {
    commit('SAVE_DELAY_TIMER', { nodeId, delayTimer, reject });
  },

  setDelayedState({ commit }, { index, nodeId, delayed, delayedKey = 'delayed' }) {
    commit('UPDATE_STACK_ITEM', { index, nodeId, [delayedKey]: delayed });
  },

  clearState({ commit, dispatch }) {
    commit('CLEAR_STATE');
    dispatch('setIsPreviewRunning', false);
  },

  clearAmpHistory({ commit }) {
    commit('CLEAR_AMP_HISTORY');
  },

  setCrmConnector({ commit }, data) {
    commit('SET_CRM_CONNECTOR', data);
  },

  setCrmLoading({ commit }, data) {
    commit('SET_CRM_LOADING', data);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
